import { Modal, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import Colors from 'src/core/utils/Colors';
import Dimens from 'src/core/utils/Dimens';
import Fonts from 'src/core/utils/Fonts';
import Translations from 'src/core/utils/Translations';
import { useAlertDialogStore } from 'src/domain/stores/useAlertDialogStore';
import useContentWidth from 'src/presentation/hooks/useContentWidth';

// The card never grows past this, and never past the column it sits in either -- see below.
const CARD_MAX_WIDTH = 360;

/**
 * The app's alert and confirmation dialog on web.
 *
 * react-native-web ships `Alert` as `static alert() {}`, so every message, confirmation and error
 * this app raises used to disappear on web: a deeplink asking "add this event?" simply hung on the
 * loader, with nothing on screen to answer. `window.confirm` was the first stand-in, but Chrome
 * ignores it outright inside a cross-origin iframe -- which is how the app is embedded on
 * topicusevents.nl/app, so the gap would come straight back in the view that matters most. This
 * dialog lives in the React tree, so it works standalone, in the drawer, and in every browser.
 *
 * Accessibility rides on react-native-web's Modal: it renders `role="dialog"` with `aria-modal`,
 * traps focus inside the card (which lands focus on the first button when it opens) and turns
 * Escape into `onRequestClose`. Dismissing resolves as a cancellation for a confirmation, but as a
 * confirmation for a notice: a single-button acknowledgement has no cancel semantics, and the
 * button behind it is what moves the app on ("account deleted" goes Home, "feedback sent" goes
 * back). Resolving it as a cancellation would drop that callback and leave the visitor on a screen
 * that should already have changed. Two details make the accessibility work rather than nearly
 * work -- no
 * `animationType`, because `role="dialog"` and the focus trap only switch on once the modal
 * reports itself shown and with an animation that hangs off an `animationend` event; and
 * `aria-label`, because the Modal gives the dialog no accessible name of its own. Remounting per
 * request (`key`) re-arms the trap when the next queued dialog takes over.
 */
function AlertDialog() {
	const request = useAlertDialogStore((state) => state.queue[0]);
	const resolve = useAlertDialogStore((state) => state.resolve);
	const pressButton = useAlertDialogStore((state) => state.pressButton);
	// The app renders in a centred column on web, so the window width is not the width available.
	const contentWidth = useContentWidth();

	if (request === undefined) {
		return null;
	}

	const { id, title, message, kind, isDestructive, buttons } = request;
	const cardWidth = Math.min(CARD_MAX_WIDTH, contentWidth - Dimens.padding20 * 2);
	const confirmLabel = request.confirmText ?? Translations.buttonOk;
	const cancelLabel = request.cancelText ?? Translations.buttonCancel;
	// An explicit list without a cancel entry yields -1, which closes the dialog and runs nothing --
	// the same as dismissing a three-button alert on Android.
	const cancelIndex = buttons === undefined ? -1 : buttons.findIndex((button) => button.isCancel === true);
	// Three labels never fit side by side in a 360px card, so a longer list stacks, the way iOS
	// stacks a three-button alert.
	const isStacked = buttons !== undefined && buttons.length > 2;

	const dismiss = () => {
		if (buttons === undefined) {
			resolve(id, kind === 'notice' ? 'confirm' : 'cancel');
			return;
		}
		pressButton(id, cancelIndex);
	};

	return (
		<Modal
			aria-label={title}
			key={id}
			testID="alert-dialog-modal"
			transparent={true}
			visible={true}
			onRequestClose={dismiss}
		>
			<View style={styles.backdrop}>
				<View style={[styles.card, { width: cardWidth }]} testID="alert-dialog">
					<Text accessibilityRole="header" style={styles.title}>
						{title}
					</Text>
					{message === null ? null : (
						<ScrollView
							contentContainerStyle={styles.messageContent}
							style={styles.messageScroll}
							testID="alert-dialog-message"
						>
							<Text style={styles.message}>{message}</Text>
						</ScrollView>
					)}
					<View style={styles.divider} />
					<View style={[styles.buttons, isStacked ? styles.buttonsStacked : null]}>
						{buttons === undefined ? (
							<>
								{kind === 'confirm' ? (
									<Pressable
										accessibilityRole="button"
										style={({ pressed }) => [styles.button, styles.cancelButton, pressed ? styles.buttonPressed : null]}
										testID="alert-dialog-cancel"
										onPress={() => resolve(id, 'cancel')}
									>
										<Text style={[styles.buttonLabel, styles.cancelLabel]}>{cancelLabel}</Text>
									</Pressable>
								) : null}
								<Pressable
									accessibilityRole="button"
									style={({ pressed }) => [
										styles.button,
										isDestructive === true ? styles.destructiveButton : styles.confirmButton,
										pressed ? styles.buttonPressed : null,
									]}
									testID="alert-dialog-confirm"
									onPress={() => resolve(id, 'confirm')}
								>
									<Text style={[styles.buttonLabel, styles.confirmLabel]}>{confirmLabel}</Text>
								</Pressable>
							</>
						) : (
							buttons.map((button, index) => (
								<Pressable
									accessibilityRole="button"
									key={index}
									style={({ pressed }) => [
										styles.button,
										button.isCancel === true
											? styles.cancelButton
											: button.isDestructive === true
												? styles.destructiveButton
												: styles.confirmButton,
										pressed ? styles.buttonPressed : null,
									]}
									testID={`alert-dialog-button-${index}`}
									onPress={() => pressButton(id, index)}
								>
									<Text style={[styles.buttonLabel, button.isCancel === true ? styles.cancelLabel : styles.confirmLabel]}>
										{button.text}
									</Text>
								</Pressable>
							))
						)}
					</View>
				</View>
			</View>
		</Modal>
	);
}

const styles = StyleSheet.create({
	backdrop: {
		flex: 1,
		alignItems: 'center',
		justifyContent: 'center',
		backgroundColor: Colors.overlay50,
		padding: Dimens.padding20,
	},
	card: {
		maxHeight: '80%',
		backgroundColor: Colors.white,
		borderRadius: 8,
		paddingTop: Dimens.padding20,
		overflow: 'hidden',
	},
	title: {
		fontFamily: Fonts.montserratMedium,
		fontSize: Dimens.fontSize18,
		color: Colors.greyHeader,
		textAlign: 'center',
		paddingHorizontal: Dimens.padding20,
		paddingBottom: Dimens.padding16,
	},
	// A long message scrolls inside the card instead of pushing the buttons off screen.
	messageScroll: {
		flexGrow: 0,
	},
	messageContent: {
		paddingHorizontal: Dimens.padding20,
		paddingBottom: Dimens.padding20,
	},
	message: {
		fontFamily: Fonts.montserratRegular,
		fontSize: Dimens.fontSize14,
		lineHeight: Dimens.lineHeight20,
		color: Colors.greyTextDark,
	},
	divider: {
		height: StyleSheet.hairlineWidth,
		backgroundColor: Colors.greyDivider,
	},
	buttons: {
		flexDirection: 'row',
		gap: Dimens.margin8,
		padding: Dimens.padding12,
	},
	buttonsStacked: {
		flexDirection: 'column',
	},
	button: {
		flex: 1,
		alignItems: 'center',
		justifyContent: 'center',
		borderRadius: 4,
		paddingVertical: Dimens.padding12,
		paddingHorizontal: Dimens.padding15,
	},
	buttonPressed: {
		opacity: 0.8,
	},
	cancelButton: {
		backgroundColor: Colors.greyBgAlt,
	},
	confirmButton: {
		backgroundColor: Colors.secondary,
	},
	destructiveButton: {
		backgroundColor: Colors.redError,
	},
	buttonLabel: {
		fontFamily: Fonts.montserratMedium,
		fontSize: Dimens.fontSize14,
		textAlign: 'center',
	},
	cancelLabel: {
		color: Colors.greyTextDark,
	},
	confirmLabel: {
		color: Colors.white,
	},
});

export default AlertDialog;
